/**
 * ForwardMessageSheet — Sheet de seleção de destinos para encaminhamento
 *
 * Fluxo:
 *  1. Mostra a(s) mensagem(ns) a encaminhar (preview compacto)
 *  2. Lista CONTATOS do tenant; para cada contato indica se há conversa
 *     na inbox de origem e se a janela 24h (Meta WABA) está aberta
 *  3. Usuário marca até 5 destinos e confirma
 *  4. RPC cria conversa na inbox de origem se necessário (WAHA/Z-API)
 *     reaproveitando get_or_create_conversation
 *
 * CHANGELOG:
 *   2026-06-13 v2.0.0 - Lista contatos (não conversas) e envia sempre pela
 *     inbox de origem; cria conversa nova se necessário (WAHA/Z-API).
 *   2026-06-13 v1.0.0 - Versão inicial.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { SmartAvatar } from '@/components/ui/smart-avatar';
import {
  Forward, Search, AlertTriangle, Loader2, Paperclip, MessageSquare, MapPin, Plus,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  useForwardMessage, FORWARD_MAX_TARGETS, type ForwardTarget,
} from '@/hooks/useForwardMessage';
import { MediaThumbnail } from './MediaThumbnail';
import type { Message } from '@/pages/tenant/Chat';

interface ForwardMessageSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Mensagens a encaminhar (multi-select suportado, até FORWARD_MAX_MESSAGES). */
  messages: Message[];
  /** Inbox de origem (sempre o envio acontece por ela). */
  sourceInboxId: string;
  /** Nome legível da inbox de origem, para exibir no cabeçalho. */
  sourceInboxName: string;
  /** Disparado APÓS encaminhamento bem-sucedido (antes do Sheet fechar). */
  onForwarded?: (jobId: string) => void;
}

const TYPE_LABELS: Record<string, string> = {
  text: 'Texto',
  image: 'Imagem',
  audio: 'Áudio',
  video: 'Vídeo',
  document: 'Documento',
  location: 'Localização',
  ptv: 'Vídeo (PTV)',
  sticker: 'Sticker',
};

function MessagePreview({ message }: { message: Message }) {
  const label = TYPE_LABELS[message.message_type] || message.message_type;
  const attachment = message.attachments?.[0];
  const text = message.content || attachment?.filename || null;

  const renderThumb = () => {
    if (
      attachment &&
      ['image', 'video', 'audio', 'document', 'sticker'].includes(message.message_type)
    ) {
      return (
        <MediaThumbnail
          type={message.message_type}
          url={attachment.url}
          filename={attachment.filename}
          mimeType={attachment.mime_type}
          size={48}
        />
      );
    }
    if (message.message_type === 'location') {
      return (
        <div
          className="rounded-lg flex items-center justify-center bg-blue-100 dark:bg-blue-900/30"
          style={{ width: 48, height: 48 }}
        >
          <MapPin className="h-5 w-5 text-blue-600 dark:text-blue-400" />
        </div>
      );
    }
    return (
      <div
        className="rounded-lg flex items-center justify-center bg-muted"
        style={{ width: 48, height: 48 }}
      >
        <MessageSquare className="h-5 w-5 text-muted-foreground" />
      </div>
    );
  };

  return (
    <div className="flex items-center gap-3 rounded-lg border bg-card px-3 py-2">
      {renderThumb()}
      <div className="flex-1 min-w-0">
        <div className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
          {label}
        </div>
        <div className="text-sm text-foreground line-clamp-2 break-words">
          {text || <span className="italic text-muted-foreground">Sem texto</span>}
        </div>
      </div>
    </div>
  );
}

export function ForwardMessageSheet({
  open, onOpenChange, messages, sourceInboxId, sourceInboxName, onForwarded,
}: ForwardMessageSheetProps) {
  const { targets, loadingTargets, loadTargets, forward, submitting } = useForwardMessage();
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Carrega contatos ao abrir
  useEffect(() => {
    if (open && sourceInboxId) {
      setSelectedIds(new Set());
      setSearch('');
      loadTargets(sourceInboxId);
    }
  }, [open, sourceInboxId, loadTargets]);

  // Debounce de busca
  useEffect(() => {
    if (!open || !sourceInboxId) return;
    const t = setTimeout(() => loadTargets(sourceInboxId, search), 300);
    return () => clearTimeout(t);
  }, [search, open, sourceInboxId, loadTargets]);

  const eligibleTargets = useMemo(
    () => targets.filter((t) => t.can_forward),
    [targets],
  );

  const toggle = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else {
        if (next.size >= FORWARD_MAX_TARGETS) return prev;
        next.add(id);
      }
      return next;
    });
  };

  const handleConfirm = async () => {
    const sourceIds = messages.map((m) => m.id);
    const targetIds = Array.from(selectedIds);
    const jobId = await forward(sourceIds, targetIds, sourceInboxId);
    if (jobId) {
      onForwarded?.(jobId);
      onOpenChange(false);
    }
  };

  const renderRow = (t: ForwardTarget) => {
    const checked = selectedIds.has(t.contact_id);
    const disabled = !t.can_forward;
    const isNewConversation = t.can_forward && !t.conversation_id;
    return (
      <button
        key={t.contact_id}
        type="button"
        disabled={disabled}
        onClick={() => toggle(t.contact_id)}
        className={cn(
          'w-full flex items-center gap-3 rounded-md px-2 py-2 text-left transition-colors',
          'hover:bg-muted focus:outline-none focus:ring-1 focus:ring-ring',
          checked && 'bg-primary/10',
          disabled && 'opacity-60 cursor-not-allowed',
        )}
      >
        <Checkbox checked={checked} disabled={disabled} className="pointer-events-none" />
        <SmartAvatar
          src={t.contact_avatar_url || undefined}
          name={t.contact_name || t.contact_phone || 'Contato'}
          size="sm"
        />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium truncate">
            {t.contact_name || t.contact_phone || 'Sem nome'}
          </div>
          <div className="text-xs text-muted-foreground truncate">
            {t.contact_phone || 'Sem telefone'}
          </div>
        </div>
        {disabled ? (
          <Badge variant="outline" className="gap-1 text-xs">
            <AlertTriangle className="h-3 w-3" /> Sem janela 24h
          </Badge>
        ) : isNewConversation ? (
          <Badge variant="secondary" className="gap-1 text-xs">
            <Plus className="h-3 w-3" /> Nova conversa
          </Badge>
        ) : null}
      </button>
    );
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md flex flex-col p-0">
        <SheetHeader className="px-4 pt-4 pb-3 border-b">
          <SheetTitle className="flex items-center gap-2">
            <Forward className="h-4 w-4" />
            {messages.length > 1
              ? `Encaminhar ${messages.length} mensagens`
              : 'Encaminhar mensagem'}
          </SheetTitle>
          <SheetDescription>
            Enviando pela inbox <span className="font-medium text-foreground">{sourceInboxName}</span>.
            Selecione até {FORWARD_MAX_TARGETS} contato(s). Para Meta WABA, é necessário ter
            uma conversa com janela 24h aberta.
          </SheetDescription>
        </SheetHeader>

        <div className="px-4 py-3 border-b max-h-[30vh] overflow-auto">
          <div className="flex items-center gap-2 mb-2">
            <Paperclip className="h-4 w-4 text-muted-foreground" />
            <h5 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              {messages.length} {messages.length === 1 ? 'mensagem selecionada' : 'mensagens selecionadas'}
            </h5>
          </div>
          <div className="space-y-2">
            {messages.map((m) => <MessagePreview key={m.id} message={m} />)}
          </div>
        </div>

        <div className="px-4 py-2 border-b">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por nome ou telefone..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8"
            />
          </div>
        </div>

        <ScrollArea className="flex-1">
          <div className="p-2 space-y-0.5">
            {loadingTargets ? (
              <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin mr-2" /> Carregando contatos...
              </div>
            ) : targets.length === 0 ? (
              <div className="text-center py-8 text-sm text-muted-foreground">
                Nenhum contato encontrado.
              </div>
            ) : (
              targets.map(renderRow)
            )}
          </div>
        </ScrollArea>

        <SheetFooter className="px-4 py-3 border-t flex-row items-center justify-between gap-2">
          <div className="text-xs text-muted-foreground">
            {selectedIds.size} / {FORWARD_MAX_TARGETS} selecionado(s)
            {eligibleTargets.length === 0 && !loadingTargets && ' · nenhum destino elegível'}
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={submitting}>
              Cancelar
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={selectedIds.size === 0 || submitting}
            >
              {submitting && <Loader2 className="h-3 w-3 animate-spin mr-1" />}
              Encaminhar
            </Button>
          </div>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
